// =====================================================================================
// INVRT Radar — web console — the reveal money path
//
// This hook is the ONLY place in the console that can spend a credit, and it cannot do so
// without a human action. The sequence mirrors the API contract exactly
// (supabase/functions/api/routes/prospecting.ts):
//
//   idle → quoting → awaiting_approval → revealing → done | failed
//
//   1. `quote(refs)`   POST /v1/leads/quote-reveal — FREE. The server canonicalises the
//                      ids, hashes them and prices the set from rate_cards. The console
//                      NEVER computes a hash or a price: the quote's payload_hash is the
//                      server's own, and the ids we send at burn time are the exact set
//                      the quote answered with (`employee_ids`), so the hashes agree by
//                      construction rather than by our re-implementing sha256.
//
//   2. APPROVE         A person clicks. Nothing before this point moves money, and there
//                      is no code path from step 1 to step 3 that does not pass through
//                      an explicit `approve()` call.
//
//   3. `approve()`     POST /v1/leads/reveal — PAID. The idempotency key is minted ONCE,
//                      at approval, and reused for every retry of that same approval —
//                      the contract's own words: "generate a fresh one per distinct
//                      reveal attempt, not per retry". Retrying with the same key replays
//                      the stored result instead of charging twice.
//
//   4. The balance query is invalidated so the header chip re-reads SUM(credit_ledger)
//      from Postgres. The response's own `balance_after` is shown as the authoritative
//      figure the moment it lands.
// =====================================================================================

import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "./useApi";
import { useSession } from "@/auth/SessionProvider";
import { newIdempotencyKey } from "@/lib/utils";
import type { QuoteResponse, RevealResponse } from "@/lib/types";

export type RevealPhase =
  | "idle"
  | "quoting"
  | "awaiting_approval"
  | "revealing"
  | "done"
  | "failed";

export interface RevealFlowState {
  phase: RevealPhase;
  quote: QuoteResponse | null;
  result: RevealResponse | null;
  error: unknown;
}

export interface RevealFlow extends RevealFlowState {
  /** Prices a set of provider refs. Free — no ledger movement of any kind. */
  quoteFor: (refs: string[]) => Promise<QuoteResponse | null>;
  /** THE SPEND. Only reachable from `awaiting_approval`, only by a human action. */
  approve: () => Promise<RevealResponse | null>;
  /** Abandons an un-approved quote. A quote left to expire costs nothing. */
  cancel: () => void;
  reset: () => void;
}

const INITIAL: RevealFlowState = { phase: "idle", quote: null, result: null, error: null };

export function useRevealFlow(): RevealFlow {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { orgId } = useSession();
  const [state, setState] = useState<RevealFlowState>(INITIAL);
  // Minted at approval and held for the life of that approval, so a retry replays rather
  // than re-charges. Deliberately NOT state: regenerating it on a re-render would turn a
  // safe retry into a second burn.
  const [idempotencyKey, setIdempotencyKey] = useState<string | null>(null);

  const reset = useCallback(() => {
    setState(INITIAL);
    setIdempotencyKey(null);
  }, []);

  const cancel = useCallback(() => {
    setState(INITIAL);
    setIdempotencyKey(null);
  }, []);

  const quoteFor = useCallback(
    async (refs: string[]): Promise<QuoteResponse | null> => {
      const unique = [...new Set(refs.map((ref) => ref.trim()).filter((ref) => ref !== ""))];
      if (unique.length === 0) {
        setState({
          phase: "failed",
          quote: null,
          result: null,
          error: new Error("Select at least one contact before requesting a quote."),
        });
        return null;
      }

      setState({ phase: "quoting", quote: null, result: null, error: null });
      setIdempotencyKey(null);

      try {
        const quote = await api.post<QuoteResponse>("/leads/quote-reveal", {
          employee_ids: unique,
        });
        setState({ phase: "awaiting_approval", quote, result: null, error: null });
        return quote;
      } catch (error) {
        setState({ phase: "failed", quote: null, result: null, error });
        return null;
      }
    },
    [api],
  );

  const approve = useCallback(async (): Promise<RevealResponse | null> => {
    const quote = state.quote;
    // The gate. There is no other caller of POST /v1/leads/reveal in this console, and it
    // refuses outright unless a quote is sitting in `awaiting_approval`.
    if (!quote || state.phase !== "awaiting_approval") return null;

    const key = idempotencyKey ?? newIdempotencyKey("radar-web-reveal");
    setIdempotencyKey(key);
    setState((current) => ({ ...current, phase: "revealing", error: null }));

    try {
      const result = await api.post<RevealResponse>("/leads/reveal", {
        quote_id: quote.quote_id,
        // The quote's OWN canonical id set where the server returned one — the same set
        // its payload_hash commits to. Falling back to nothing is safer than guessing.
        employee_ids: quote.employee_ids ?? [],
        idempotency_key: key,
      });

      setState({ phase: "done", quote, result, error: null });
      // Credits moved (or deliberately did not, in the unbilled case). Either way the
      // header must re-read the ledger rather than trust a local figure.
      void queryClient.invalidateQueries({ queryKey: ["balance", orgId] });
      void queryClient.invalidateQueries({ queryKey: ["ledger", orgId] });
      return result;
    } catch (error) {
      // Keep the quote: a rate limit or a transient fault is worth retrying under the
      // SAME idempotency key, which is exactly what pressing approve again now does.
      setState({ phase: "awaiting_approval", quote, result: null, error });
      return null;
    }
  }, [api, idempotencyKey, orgId, queryClient, state.phase, state.quote]);

  return useMemo(
    () => ({ ...state, quoteFor, approve, cancel, reset }),
    [state, quoteFor, approve, cancel, reset],
  );
}
